import React from "react";
import s from "./style.module.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import dataset from "../../data/data.json";
import FadeIn from "react-fade-in";
import { scaleLinear } from "d3-scale";
import { wordwrap } from "d3-jetpack";

const margin = {
  top: 20,
  right: 50,
  bottom: 10,
  left: 50,
};

const Affordability = ({
  data,
  title,
  section,
  note,
  source,
  sectionIndex,
  index = 0,
  id,
  circleLabels,
  scrollerNum = 0,
  highIncome,
  lowIncome,
  countries,
  indicatorLabels,
  indicatorDescriptions,
  upperMiddleIncome,
  ...rest
}) => {
  const { ref, width, windowSize } = useVizualisation();

  const height = Math.min(640, windowSize.height);

  const low = dataset.filter(
    (d) =>
      d.dimension === "people" && d.position === "left" && d.section === section
  );

  const high = dataset.filter(
    (d) =>
      d.dimension === "people" &&
      d.position === "right" &&
      d.section === section
  );

  const w = Math.max(width, 550) - margin.left - margin.right;
  const h = height - margin.top - margin.bottom;

  const R100 = Math.sqrt(100 / Math.PI);

  const r = scaleLinear().domain([0, R100]).range([0, 50]);

  return (
    <Vizualisation ref={ref} note={note} source={source} size="large">
      <svg viewBox={`0 0 ${Math.max(width, 550)} ${height}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g className="low-income">
            <text>
              <tspan style={{ fontWeight: "bold" }}>{lowIncome}</tspan>{" "}
              {countries}
            </text>
            <FadeIn wrapperTag="g" childTag="g">
              {low
                .filter((d, i) => i <= index)
                .map((d, i) => (
                  <g
                    key={`low-state-${i}`}
                    transform={`translate(0, ${60 + i * 280})`}
                  >
                    }
                    <text dy={-17} className={s.indicatorLabel}>
                      {indicatorLabels[d.step_id]}
                    </text>
                    <text className={s.statLabel}>
                      {wordwrap(
                        indicatorDescriptions[d.step_id],
                        width >= 550 ? 45 : 35
                      ).map((l, i) => (
                        <tspan key={`label-tokebn-${i}`} x={0} y={i * 14}>
                          {l}
                        </tspan>
                      ))}
                    </text>
                    <text dy={30} className={s.statNote}>
                      {upperMiddleIncome}
                    </text>
                    <g transform="translate(100, 160)">
                      <circle
                        r={r(Math.sqrt(d.percent / Math.PI))}
                        fill="#EE6047"
                      />
                      <path
                        d={`M ${-r(R100)}, 0 a ${r(R100)},${r(R100)} 0 1,1 ${
                          2 * r(R100)
                        },0 a ${r(R100)},${r(R100)} 0 1,1 ${-2 * r(R100)},0`}
                        id={`low-${i}`}
                        fill="none"
                        stroke="black"
                        style={{
                          strokeDasharray: "4 2",
                        }}
                        transform="rotate(-10)"
                      />
                      <text dy={-4}>
                        <textPath href={`#low-${i}`} startOffset={87}>
                          {circleLabels[i].text}
                        </textPath>
                      </text>
                      <text className={s.percentage} dy={-2}>
                        {d.display_percent}%
                      </text>
                    </g>
                  </g>
                ))}
            </FadeIn>
          </g>
          <g
            className="high-income"
            transform={`translate(${w}, 0)`}
            style={{ textAnchor: "end" }}
          >
            <text className="subHeading">
              <tspan style={{ fontWeight: "bold" }}>{highIncome}</tspan>{" "}
              {countries}
            </text>
            <FadeIn wrapperTag="g" childTag="g">
              {high
                .filter((d, i) => i <= index)
                .map((d, i) => (
                  <g
                    key={`high-state-${i}`}
                    transform={`translate(${-100 * 2}, ${60 + i * 280})`}
                  >
                    <text x={100 * 2} dy={-17} className={s.indicatorLabel}>
                      {indicatorLabels[d.step_id]}
                    </text>
                    <text x={100 * 2} className={s.statLabel}>
                      {wordwrap(
                        indicatorDescriptions[d.step_id],
                        width >= 550 ? 45 : 35
                      ).map((l, i) => (
                        <tspan
                          key={`stat-label-token-${i}`}
                          y={i * 14}
                          x={100 * 2}
                        >
                          {l}
                        </tspan>
                      ))}
                    </text>
                    <text dy={30} x={100 * 2} className={s.statNote}>
                      {upperMiddleIncome}
                    </text>
                    <g transform="translate(100, 160)">
                      <circle
                        r={r(Math.sqrt(d.percent / Math.PI))}
                        fill="#481F7C"
                      />
                      <path
                        d={`M ${-r(R100)}, 0 a ${r(R100)},${r(R100)} 0 1,1 ${
                          2 * r(R100)
                        },0 a ${r(R100)},${r(R100)} 0 1,1 ${-2 * r(R100)},0`}
                        id={`high-${i}`}
                        fill="none"
                        stroke="black"
                        style={{
                          strokeDasharray: "4 2",
                        }}
                        transform="rotate(-10)"
                      />
                      <text dy={-4}>
                        <textPath href={`#high-${i}`} startOffset={87}>
                          {circleLabels[i].text}
                        </textPath>
                      </text>
                      <text dy={-2} className={s.percentage} fill="white">
                        {d.display_percent}%
                      </text>
                    </g>
                  </g>
                ))}
            </FadeIn>
          </g>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default Affordability;
